import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { VelocityComponent } from 'velocity-react';

import { Button, Icon, Link } from '../../components/';
import AccountList from '../../user/accountList/AccountList';
import { VelocityDropdown, DURATION_DROPDOWN_TOGGLE } from '../../animation';
import { TranslatedCopy } from '../../intl/TranslatedCopy';

import { showToast } from '../../components/toast/actions';
import { toggleAccountMenu, toggleNavMenu } from '../actions';
import { getUserInvoiceAuth } from '../../auth/actions';
import { getUserAccount } from '../../user/selectors';
import { setUserAccount } from '../../user/actions';
import { selectShopDepartment } from '../../shop/actions';
import { selectIsAccountMenuOpen } from '../reducer';

import { userTypes, userAccountTypes } from '../../types/user';

const standardPadding = 'pt4-sm pb4-sm prl4-sm';
const hideUntilDesktop = 'd-sm-h d-lg-ib';

export class AccountDropdown extends Component {
  constructor(props) {
    super(props);
    this.state = {
      accountsList: []
    };
  }

  componentDidMount() {
    const { userData, userAccount } = this.props;
    const accounts = userAccount && userData.accountDepartments;
    this.setState({ accountsList: accounts });
    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
  }

  setRef = node => (this.dropdownRef = node);

  setButtonRef = node => (this.buttonRef = node);

  handleLogout = e => {
    const { logout } = this.props;
    logout(e);
  };

  handleClickOutside = event => {
    if (this.dropdownRef && this.buttonRef) {
      const { isAccountMenuOpen, toggleAccountMenu } = this.props;
      if (!this.dropdownRef.contains(event.target) && !this.buttonRef.contains(event.target) && isAccountMenuOpen) {
        toggleAccountMenu();
      }
    }
  };

  handleLinkClick = () => {
    const { toggleAccountMenu, toggleNavMenu } = this.props;
    toggleAccountMenu();
    toggleNavMenu();
  };

  handleAccountClick = ({ id, name, number }) => {
    const { selectShopDepartment, setUserAccount, showToast, toggleAccountMenu } = this.props;

    const newAccount = {
      id,
      name,
      number
    };

    setUserAccount(newAccount);
    localStorage.setItem('userData', JSON.stringify(newAccount));
    showToast({ type: 'success', message: new TranslatedCopy({ id: 'accountDropdown.toast.switched' }).toString() });
    if (toggleAccountMenu) {
      toggleAccountMenu();
    }
    selectShopDepartment(null);
  };

  render() {
    const { isAccountMenuOpen, userData, userAccount, toggleAccountMenu } = this.props;
    const { accountsList } = this.state;
    return (
      <>
        <div ref={this.setButtonRef}>
          <Button
            type='button'
            className='ncss-btn-white p0-sm pl2-sm'
            onClick={toggleAccountMenu}
            data-desc='accountDropdown__button_toggle-menu'
          >
            {/* Mobile icon */}
            <span className='d-lg-h'>
              <VelocityComponent
                animation={isAccountMenuOpen ? { rotateZ: '180deg' } : { rotateZ: '90deg' }}
                duration={DURATION_DROPDOWN_TOGGLE}
              >
                <Icon name='chevron-up-cell' alt={`${isAccountMenuOpen ? 'Close' : 'Open'} account menu`} />
              </VelocityComponent>
            </span>
            {/* Desktop icon */}
            <span className={hideUntilDesktop}>
              <VelocityComponent
                animation={isAccountMenuOpen ? { rotateZ: '0deg' } : { rotateZ: '-180deg' }}
                duration={DURATION_DROPDOWN_TOGGLE}
              >
                <Icon name='chevron-up-cell' alt={`${isAccountMenuOpen ? 'Close' : 'Open'} account menu`} />
              </VelocityComponent>
            </span>
          </Button>
        </div>
        <div ref={this.setRef}>
          <VelocityDropdown
            show={isAccountMenuOpen}
            containerClasses='accountInfo body-3 bg-secondary-grey flex-container space-between'
          >
            <div className={classnames(hideUntilDesktop, 'text-color-secondary body-4 pt5-sm pb2-sm prl4-sm')}>
              <div>
                {userData.firstName}&nbsp;
                {userData.lastName}
              </div>
              <div>{userData.email}</div>
              <Link
                to='/profile/info'
                className={classnames(hideUntilDesktop, 'text-color-secondary text-underline')}
                onClick={this.handleLinkClick}
                data-desc='accountDropdown__link_profile'
              >
                <TranslatedCopy id='global.profile' />
              </Link>
            </div>
            <div className='flex-container align-center'>
              <div className={classnames(standardPadding, 'pb5-sm border-bottom u-full-width')}>
                <Link
                  to='/account/manage'
                  className='accountInfo__manage flex-container start space-between'
                  onClick={this.handleLinkClick}
                  data-desc='accountDropdown__link_manage-account'
                >
                  {userAccount && (
                    <div className='u-no-ws-e' style={{ width: '100%' }}>
                      {userAccount.number} {userAccount.name}
                    </div>
                  )}
                  <div className='body-4 text-color-secondary'>
                    <TranslatedCopy id='accountDropdown.signIn' />
                  </div>
                </Link>
              </div>
              {accountsList && accountsList.length > 0 && (
                <AccountList accounts={accountsList} handleAccountClick={this.handleAccountClick} />
              )}
            </div>
            <ul>
              {/* ImportAccountModal removed as users no longer require the ability to merge accounts */}
              {/* If this must be reimplemented, reference PR 2270 - Remove Ability to Import Accounts */}
              <li className={classnames(standardPadding, 'border-top d-lg-h')}>
                <Link
                  to='/profile/info'
                  className='accountInfo__link'
                  onClick={this.handleLinkClick}
                  data-desc='accountDropdown__link_edit-profile'
                >
                  <TranslatedCopy id='global.profile' />
                </Link>
              </li>
              <li className={classnames(standardPadding, 'border-top')}>
                <button
                  className='accountInfo__link body-3 p0-sm ta-sm-l u-full-width'
                  type='button'
                  onClick={this.handleLogout}
                  data-desc='accountDropdown__button_sign-out'
                >
                  <TranslatedCopy id='global.signOut' />
                </button>
              </li>
            </ul>
          </VelocityDropdown>
        </div>
      </>
    );
  }
}

AccountDropdown.propTypes = {
  isAccountMenuOpen: PropTypes.bool.isRequired,
  logout: PropTypes.func.isRequired,
  selectShopDepartment: PropTypes.func.isRequired,
  setUserAccount: PropTypes.func.isRequired,
  showToast: PropTypes.func.isRequired,
  toggleAccountMenu: PropTypes.func.isRequired,
  toggleNavMenu: PropTypes.func.isRequired,
  userData: userTypes,
  userAccount: userAccountTypes
};

AccountDropdown.defaultProps = {
  userData: {
    firstName: '',
    lastName: '',
    locale: '',
    email: '',
    timeZone: '',
    accountDepartments: []
  },
  userAccount: null
};

const mapStateToProps = state => ({
  isAccountMenuOpen: selectIsAccountMenuOpen(state),
  userAccount: getUserAccount(state)
});

export default connect(mapStateToProps, {
  getUserInvoiceAuth,
  selectShopDepartment,
  setUserAccount,
  showToast,
  toggleAccountMenu,
  toggleNavMenu
})(AccountDropdown);
